import { useState, useEffect } from 'react';
import { getRules, createRule, updateRule, deleteRule } from '../api/automation-rules';
import { getCompanies } from '../api/companies';
import { Topbar } from '../layout/Topbar';
import type { AutomationRule, Company } from '../types';

export function AutomationRulesPage() {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRule, setSelectedRule] = useState<AutomationRule | null>(null);
  const [name, setName] = useState('');
  const [terms, setTerms] = useState('');
  const [department, setDepartment] = useState('');
  const [subject, setSubject] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    getCompanies().then(setCompanies).catch(() => console.error('Falha ao carregar empresas'));
    getRules()
      .then(setRules)
      .catch(() => console.error('Falha ao carregar regras'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (rule: AutomationRule) => {
    setSelectedRule(rule);
    setName(rule.name);
    setTerms((rule.conditions?.terms || []).join(', '));
    setDepartment(rule.actions?.department || '');
    setSubject(rule.actions?.subject || '');
    setIsActive(rule.isActive);
    setCompanyId(rule.companyId);
  };

  const handleNew = () => {
    setSelectedRule(null);
    setName('');
    setTerms('');
    setDepartment('');
    setSubject('');
    setIsActive(true);
    setCompanyId(companies[0]?.id || '');
  };

  const handleSave = async () => {
    if (!name || !department) return;
    const termsArray = terms.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      if (selectedRule) {
        const updated = await updateRule(selectedRule.id, {
          name,
          conditions: { terms: termsArray },
          actions: { department, subject: subject || name },
          isActive,
        });
        setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } else {
        const created = await createRule({
          name,
          triggerType: 'INTENT_DETECTED',
          conditions: { terms: termsArray },
          actions: { department, subject: subject || name },
          companyId,
        });
        setRules((prev) => [...prev, created]);
      }
      handleNew();
    } catch (err) {
      console.error('Falha ao salvar regra:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedRule) return;
    try {
      await deleteRule(selectedRule.id);
      setRules((prev) => prev.filter((r) => r.id !== selectedRule.id));
      handleNew();
    } catch (err) {
      console.error('Falha ao excluir regra:', err);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Regras de Automação" subtitle="Regras de triagem para classificação de intenção." />

      <div className="records-layout">
        <div className="panel">
          <div className="panel-header">
            <h2>Regras de triagem</h2>
            <button onClick={handleNew}>+ Nova</button>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : rules.length === 0 ? (
            <div className="empty-state"><p>Nenhuma regra de triagem configurada.</p></div>
          ) : (
            <div className="data-list">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="data-list-item"
                  onClick={() => handleSelect(rule)}
                  style={{ background: selectedRule?.id === rule.id ? 'var(--color-primary-bg)' : undefined }}
                >
                  <div>
                    <div className="item-name">{rule.name}</div>
                    <div className="item-detail">{rule.actions?.department} — {rule.conditions?.terms?.length || 0} termos</div>
                  </div>
                  <span className={`pill ${rule.isActive ? 'success' : 'neutral'}`}>
                    {rule.isActive ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>{selectedRule ? 'Editar' : 'Nova'} regra</h2>
          </div>
          <div className="panel-body">
            <div className="form-stack">
              <label>
                Empresa
                <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label>
                Nome
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Suporte Técnico" />
              </label>
              <label>
                Setor
                <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                  <option value="">Selecione</option>
                  <option value="Financeiro">Financeiro</option>
                  <option value="Suporte Técnico">Suporte Técnico</option>
                  <option value="Vendas">Vendas</option>
                  <option value="Administrativo">Administrativo</option>
                </select>
              </label>
              <label>
                Assunto
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex: Suporte técnico" />
              </label>
              <label>
                Palavras-chave (separadas por vírgula)
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={3}
                  placeholder="sem internet, lentidão, wifi, suporte"
                />
              </label>
              <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                Regra ativa
              </label>
              <div className="flex gap-sm">
                <button onClick={handleSave}>Salvar</button>
                <button className="secondary" onClick={handleNew}>Cancelar</button>
                {selectedRule && <button className="danger" onClick={handleDelete}>Excluir</button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
